/**
 * Sprites for the particle systems.
 *
 * Separated from the effects system so each one can be rendered and looked at on its own,
 * which is how the smoke sprite's problem was finally found: reasoning about compositing
 * operations from the call site is unreliable, and a 128 px bitmap either has a lumpy
 * outline or it does not.
 *
 * The rule these share: a particle's silhouette IS the effect. Billboarded quads all show
 * the same shape from every angle, so whatever the alpha channel's outline looks like is
 * what the player sees a few hundred times over. A radial gradient's outline is a circle,
 * and no amount of colour, count or size tuning will make a field of circles read as
 * anything but bubbles.
 */

import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';
import type { Texture } from '@babylonjs/core/Materials/Textures/texture';
import type { Scene } from '@babylonjs/core/scene';

import { Rng } from '../../sim/rng';

type Ctx2D = CanvasRenderingContext2D;

/**
 * A lobe with a hard shoulder: solid to `core` of its radius, then a short falloff.
 *
 * The shoulder is the whole trick. Soft radial gradients sum into a smooth mound no
 * matter how many are stacked or where they are placed, because each contributes most of
 * its weight at its own centre. Giving each lobe a flat top and a short edge means the
 * union's boundary is the union of their boundaries — bumpy — instead of an average.
 */
function hardLobe(ctx: Ctx2D, cx: number, cy: number, r: number, alpha: number, core = 0.72): void {
  const g = ctx.createRadialGradient(cx, cy, r * core, cx, cy, r);
  g.addColorStop(0, `rgba(255,255,255,${alpha})`);
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();
}

/**
 * Billowing smoke — an irregular mass with a turbulent, bitten-out edge.
 *
 * Smoke wants the opposite construction to spray. Spray is separated droplets with gaps
 * between them; smoke is one continuous body whose silhouette is turbulent — the
 * cauliflower boundary of hot gas shouldering into still air. What they have in common is
 * that neither outline is a circle.
 */
export function createSmokeTexture(scene: Scene, size = 128): Texture {
  const tex = new DynamicTexture('fxSmoke', size, scene, false);
  const ctx = tex.getContext() as unknown as Ctx2D;
  const rng = new Rng('fx-smoke');
  ctx.clearRect(0, 0, size, size);

  /*
   * The body: lobes pushed well out from the centre, each small enough that its own edge
   * shows in the union. Reaching to about 0.42 of the texture leaves clear room inside
   * the feather below, so the silhouette that survives is the lobes' and not the fade's.
   */
  for (let i = 0; i < 22; i++) {
    const a = rng.range(0, Math.PI * 2);
    const rr = rng.range(0.05, 0.24);
    hardLobe(
      ctx,
      size * (0.5 + Math.cos(a) * rr),
      size * (0.5 + Math.sin(a) * rr),
      size * rng.range(0.10, 0.18),
      rng.range(0.55, 0.95),
    );
  }

  /*
   * Erode the rim. Without this the lobes still tend toward a round envelope simply
   * because they are packed around a point; the bites are what put concavities in the
   * outline, and concavity is the thing a circle can never have.
   */
  ctx.globalCompositeOperation = 'destination-out';
  for (let i = 0; i < 16; i++) {
    const a = rng.range(0, Math.PI * 2);
    const rr = rng.range(0.26, 0.44);
    hardLobe(
      ctx,
      size * (0.5 + Math.cos(a) * rr),
      size * (0.5 + Math.sin(a) * rr),
      size * rng.range(0.09, 0.17),
      rng.range(0.75, 1),
      0.45,
    );
  }

  // Thin the interior unevenly so the body is not a flat slab of alpha.
  for (let i = 0; i < 10; i++) {
    const a = rng.range(0, Math.PI * 2);
    const rr = rng.range(0, 0.2);
    hardLobe(
      ctx,
      size * (0.5 + Math.cos(a) * rr),
      size * (0.5 + Math.sin(a) * rr),
      size * rng.range(0.06, 0.13),
      rng.range(0.12, 0.3),
      0.2,
    );
  }

  /*
   * Clear the corners, and only the corners.
   *
   * This starts at 0.46 rather than 0.40 on purpose. A circular ramp applied across the
   * band where the outline actually lives will smooth that outline back into a circle —
   * which is precisely what the first version of this texture did, and why the smoke was
   * still drawing as black discs after the lobes were added.
   */
  const fade = ctx.createRadialGradient(size / 2, size / 2, size * 0.46, size / 2, size / 2, size * 0.5);
  fade.addColorStop(0, 'rgba(0,0,0,0)');
  fade.addColorStop(1, 'rgba(0,0,0,1)');
  ctx.fillStyle = fade;
  ctx.fillRect(0, 0, size, size);
  ctx.globalCompositeOperation = 'source-over';

  tex.update();
  tex.hasAlpha = true;
  return tex;
}

/**
 * Torn spray — a ragged clump of droplets and stretched sheets, with real gaps in it.
 *
 * Water leaving the surface separates. The gaps are as important as the droplets: they
 * are what distinguishes thrown water from the continuous mass of smoke above.
 */
export function createSprayTexture(scene: Scene, size = 128): Texture {
  const tex = new DynamicTexture('fxSpray', size, scene, false);
  const ctx = tex.getContext() as unknown as Ctx2D;
  const rng = new Rng('fx-spray');
  ctx.clearRect(0, 0, size, size);

  // Torn ligaments: the stretched sheets of water between droplet clusters.
  for (let i = 0; i < 9; i++) {
    ctx.save();
    ctx.translate(size * rng.range(0.32, 0.68), size * rng.range(0.3, 0.7));
    ctx.rotate(rng.range(-0.6, 0.6));
    ctx.scale(rng.range(0.35, 0.6), rng.range(1.3, 2.2));
    hardLobe(ctx, 0, 0, size * rng.range(0.07, 0.13), rng.range(0.5, 0.8), 0.3);
    ctx.restore();
  }

  // Droplets, densest at the core and sparse toward the torn edge.
  for (let i = 0; i < 46; i++) {
    // Biasing the radial sample keeps a dense core while still reaching the rim.
    const rr = Math.pow(rng.next(), 0.62) * 0.44;
    const a = rng.range(0, Math.PI * 2);
    hardLobe(
      ctx,
      size * (0.5 + Math.cos(a) * rr),
      size * (0.5 + Math.sin(a) * rr),
      Math.max(1, size * rng.range(0.012, 0.05) * (1 - rr * 0.8)),
      rng.range(0.6, 1),
      0.45,
    );
  }

  const fade = ctx.createRadialGradient(size / 2, size / 2, size * 0.46, size / 2, size / 2, size * 0.5);
  fade.addColorStop(0, 'rgba(0,0,0,0)');
  fade.addColorStop(1, 'rgba(0,0,0,1)');
  ctx.globalCompositeOperation = 'destination-out';
  ctx.fillStyle = fade;
  ctx.fillRect(0, 0, size, size);
  ctx.globalCompositeOperation = 'source-over';

  tex.update();
  tex.hasAlpha = true;
  return tex;
}

/** Soft radial sprite — for mist and flash, the two things that genuinely are formless. */
export function createSoftSprite(scene: Scene, size = 64, hardness = 0.35): Texture {
  const tex = new DynamicTexture('fxSprite', size, scene, false);
  const ctx = tex.getContext() as unknown as Ctx2D;
  const g = ctx.createRadialGradient(size / 2, size / 2, size * hardness * 0.2, size / 2, size / 2, size / 2);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(hardness, 'rgba(255,255,255,0.72)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  tex.update();
  tex.hasAlpha = true;
  return tex;
}
